/**
 * Modal for providing source content when automated fetch fails.
 * Two-step flow: upload → preview → confirm
 */

import { useRef, useState } from 'react'
import { resultsApi } from '../services/api'

interface ProvideSourceModalProps {
  sourceUrl: string
  onClose: () => void
  onSuccess: () => void
}

type InputType = 'file' | 'text'
type Step = 'upload' | 'preview'

const MAX_TEXT_LENGTH = 1024 * 1024 // 1MB
const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB

interface UploadResult {
  success: boolean
  message: string
  source_id?: string
  url: string
  text_preview: string
  word_count: number
}

export const ProvideSourceModal = ({ sourceUrl, onClose, onSuccess }: ProvideSourceModalProps) => {
  const [step, setStep] = useState<Step>('upload')
  const [inputType, setInputType] = useState<InputType>('text')
  const [textContent, setTextContent] = useState('')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      if (file.size > MAX_FILE_SIZE) {
        setError(`File too large. Maximum size is ${MAX_FILE_SIZE / (1024 * 1024)}MB.`)
        setSelectedFile(null)
        event.target.value = '' // Reset file input
        return
      }
      setSelectedFile(file)
      setError(null)
    }
  }

  const handleUpload = async () => {
    if (inputType === 'file' && !selectedFile) {
      setError('Please select a file')
      return
    }
    if (inputType === 'file' && selectedFile && selectedFile.size > MAX_FILE_SIZE) {
      setError(`File too large. Maximum size is ${MAX_FILE_SIZE / (1024 * 1024)}MB.`)
      return
    }
    if (inputType === 'text' && !textContent.trim()) {
      setError('Please paste some content')
      return
    }
    if (inputType === 'text' && textContent.length > MAX_TEXT_LENGTH) {
      setError('Content exceeds 1MB limit')
      return
    }

    setUploading(true)
    setError(null)

    try {
      const result = await resultsApi.provideSourceContent(sourceUrl, {
        file: inputType === 'file' ? selectedFile! : undefined,
        textContent: inputType === 'text' ? textContent : undefined,
      })

      setUploadResult(result)
      setStep('preview')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed')
    } finally {
      setUploading(false)
    }
  }

  const handleConfirm = () => {
    onSuccess()
  }

  const handleBack = () => {
    setStep('upload')
    setUploadResult(null)
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '8px',
          padding: '2rem',
          maxWidth: '600px',
          width: '90%',
          maxHeight: '80vh',
          overflow: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {step === 'upload' ? (
          <>
            <h2 style={{ marginTop: 0 }}>Provide Source Content</h2>

            <p style={{ color: '#555', marginBottom: '1rem' }}>
              The source URL could not be fetched automatically. Provide the content manually.
            </p>

            {/* URL Display */}
            <div
              style={{
                background: '#e3f2fd',
                padding: '0.75rem 1rem',
                borderRadius: '4px',
                marginBottom: '1.5rem',
                border: '1px solid #90caf9',
              }}
            >
              <div style={{ fontSize: '0.85rem', color: '#1565c0', marginBottom: '0.25rem' }}>
                Content will be cached for:
              </div>
              <div
                style={{
                  fontSize: '0.9rem',
                  wordBreak: 'break-all',
                  fontFamily: 'monospace',
                }}
              >
                {sourceUrl}
              </div>
            </div>

            {/* Input Type Toggle */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                How would you like to provide the content?
              </label>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    checked={inputType === 'text'}
                    onChange={() => setInputType('text')}
                    style={{ marginRight: '0.5rem' }}
                  />
                  Paste Text
                </label>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    checked={inputType === 'file'}
                    onChange={() => setInputType('file')}
                    style={{ marginRight: '0.5rem' }}
                  />
                  Upload File
                </label>
              </div>
            </div>

            {/* Input Area */}
            {inputType === 'text' ? (
              <div style={{ marginBottom: '1rem' }}>
                <textarea
                  value={textContent}
                  onChange={(e) => setTextContent(e.target.value)}
                  placeholder="Paste the source content here..."
                  style={{
                    width: '100%',
                    minHeight: '200px',
                    padding: '0.75rem',
                    borderRadius: '4px',
                    border: '1px solid #ddd',
                    fontSize: '0.95rem',
                    resize: 'vertical',
                  }}
                />
                <div
                  style={{
                    fontSize: '0.85rem',
                    color: textContent.length > MAX_TEXT_LENGTH ? '#e74c3c' : '#7f8c8d',
                    textAlign: 'right',
                  }}
                >
                  {(textContent.length / 1024).toFixed(1)} KB / 1MB
                </div>
              </div>
            ) : (
              <div style={{ marginBottom: '1rem' }}>
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileSelect}
                  accept=".pdf,.txt,.html,.htm,.md,.markdown"
                  style={{ display: 'none' }}
                />
                <div
                  onClick={() => fileInputRef.current?.click()}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      fileInputRef.current?.click()
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label={
                    selectedFile
                      ? `Selected: ${selectedFile.name}. Press to change file`
                      : 'Select file to upload'
                  }
                  style={{
                    border: `2px dashed ${selectedFile ? '#27ae60' : '#bdc3c7'}`,
                    borderRadius: '8px',
                    padding: '1.5rem',
                    textAlign: 'center',
                    cursor: 'pointer',
                    background: selectedFile ? '#f0faf3' : '#fafafa',
                    transition: 'border-color 0.2s, background 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    if (!selectedFile) {
                      e.currentTarget.style.borderColor = '#3498db'
                      e.currentTarget.style.background = '#f0f7fd'
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!selectedFile) {
                      e.currentTarget.style.borderColor = '#bdc3c7'
                      e.currentTarget.style.background = '#fafafa'
                    }
                  }}
                >
                  {selectedFile ? (
                    <>
                      <div style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#27ae60' }}>
                        {selectedFile.name}
                      </div>
                      <div style={{ fontSize: '0.85rem', color: '#555', marginTop: '0.25rem' }}>
                        {(selectedFile.size / 1024).toFixed(1)} KB — Click to change file
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ fontSize: '1.1rem', color: '#555', marginBottom: '0.25rem' }}>
                        Click to select a file
                      </div>
                      <div style={{ fontSize: '0.85rem', color: '#7f8c8d' }}>
                        PDF, TXT, HTML, Markdown (max 10MB)
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Error */}
            {error && (
              <div
                style={{
                  background: '#fadbd8',
                  color: '#e74c3c',
                  padding: '0.75rem',
                  borderRadius: '4px',
                  marginBottom: '1rem',
                }}
              >
                {error}
              </div>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button
                onClick={onClose}
                disabled={uploading}
                style={{
                  padding: '0.5rem 1rem',
                  background: '#95a5a6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: uploading ? 'not-allowed' : 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleUpload}
                disabled={uploading}
                style={{
                  padding: '0.5rem 1rem',
                  background: '#3498db',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: uploading ? 'not-allowed' : 'pointer',
                }}
              >
                {uploading ? 'Processing...' : 'Upload & Preview'}
              </button>
            </div>
          </>
        ) : (
          /* Preview Step */
          <>
            <h2 style={{ marginTop: 0 }}>Confirm Source Content</h2>

            <div
              style={{
                background: '#e8f5e9',
                padding: '1rem',
                borderRadius: '4px',
                marginBottom: '1rem',
                border: '1px solid #81c784',
              }}
            >
              <div style={{ fontWeight: 'bold', color: '#2e7d32', marginBottom: '0.5rem' }}>
                Content extracted successfully
              </div>
              <div style={{ fontSize: '0.9rem', color: '#555' }}>
                <strong>{uploadResult?.word_count}</strong> words extracted
              </div>
            </div>

            {/* URL */}
            <div style={{ marginBottom: '1rem' }}>
              <div style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>For URL:</div>
              <div
                style={{
                  background: '#f5f5f5',
                  padding: '0.5rem',
                  borderRadius: '4px',
                  fontSize: '0.85rem',
                  fontFamily: 'monospace',
                  wordBreak: 'break-all',
                }}
              >
                {uploadResult?.url}
              </div>
            </div>

            {/* Preview */}
            <div style={{ marginBottom: '1rem' }}>
              <div style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>Content Preview:</div>
              <div
                style={{
                  background: '#f5f5f5',
                  padding: '0.75rem',
                  borderRadius: '4px',
                  fontSize: '0.9rem',
                  maxHeight: '200px',
                  overflow: 'auto',
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'inherit',
                }}
              >
                {uploadResult?.text_preview}
              </div>
            </div>

            {/* Warning */}
            <div
              style={{
                background: '#fff3e0',
                padding: '0.75rem',
                borderRadius: '4px',
                marginBottom: '1rem',
                border: '1px solid #ffb74d',
                fontSize: '0.9rem',
              }}
            >
              <strong>Note:</strong> This content will be used for all fact verifications
              referencing this URL. Make sure it matches the expected source.
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button
                onClick={handleBack}
                style={{
                  padding: '0.5rem 1rem',
                  background: '#95a5a6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                Back
              </button>
              <button
                onClick={handleConfirm}
                style={{
                  padding: '0.5rem 1rem',
                  background: '#27ae60',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                Confirm & Use This Content
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
